import { Timestamp } from 'firebase/firestore';
import React from 'react';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import ButtonDesigned from '../../../../components/UI-Elements/Buttons/ButtonDesigned';
import MetricCard from '../../../../components/UI-Elements/MetricCard';
import WideMetricCard from '../../../../components/UI-Elements/WideMetricCard';
import { IBalanceFlowProps, IBalanceInstruction } from '../../../../sp-types/balanceFlowType';
import { updateBalanceStepsStatusByIndexes } from '../../balanceUtils';
import { BalanceFlowTransactionApprovalDisclaimerDiv } from './BalanceFlowSubComponentsTransactionApproval';
import {
  getBalanceTransactionApprovalCards,
  getBalanceWideMetricCardsSplit,
} from './BalanceFlowTransactionApprovalUtils';

export const BalanceFlowTransactionApproval: React.FC<IBalanceFlowProps> = ({
  balanceFlowData,
  updateBalanceFlowData,
  currentStepIndex,
  setCurrentStepIndex,
  setIsLoading,
}) => {
  const { materialsMap } = useMaterialsStore();
  const { balanceInstructions } = balanceFlowData;

  const updateInstructionsEditValues = (instructions: IBalanceInstruction[]): IBalanceInstruction[] =>
    instructions.map(({ numberOfContracts, lastPrice, ...rest }) => ({
      ...rest,
      numberOfContracts,
      lastPrice,
      executedNumberOfContracts: numberOfContracts,
      executedPrice: lastPrice,
      status: 'executed',
    }));

  async function updateBalanceFlow() {
    setIsLoading(true);

    const updatedBalanceInstructions = balanceInstructions && updateInstructionsEditValues(balanceInstructions);

    await updateBalanceFlowData({
      ...balanceFlowData,
      balanceInstructions: updatedBalanceInstructions,
      steps: updateBalanceStepsStatusByIndexes(balanceFlowData.steps, [
        { index: currentStepIndex, status: 'Approved' },
        { index: currentStepIndex + 1, status: 'Incomplete' },
        { index: currentStepIndex + 2, status: 'Incomplete', doNotUpdateStatuses: ['Approved', 'Pending'] },
      ]),
    }),
      setCurrentStepIndex(currentStepIndex + 1);
    setIsLoading(false);
  }

  return balanceInstructions?.length === 0 || !balanceInstructions ? (
    <div>Execution Instruction are being generated </div>
  ) : (
    <div className="flex flex-1 flex-col space-y-10  ">
      <div className="flex justify-between">
        <h2 className="text-lg ">Transaction approval</h2>
        <h2 className="text-base font-normal mt-auto text-red ">Finalize your protection!</h2>
      </div>
      <div className="flex flex-row  justify-stretch flex-wrap space-x-[0.75rem] items-start">
        {getBalanceTransactionApprovalCards(
          materialsMap[balanceFlowData.formulaId].name,
          getTotalQuantity(balanceFlowData.balanceInstructions || []),
          getInstructionDateRange(balanceFlowData.balanceInstructions || []),
          (balanceFlowData.orderedRoutes || [])[0],
        ).map((item, index) => (
          <MetricCard cards={item.cards} style={item.style} key={index} />
        ))}
      </div>
      <div className="flex space-x-[5rem]">
        <div className="space-y-[0.5rem] flex flex-col justify-between basis-1/2">
          {getBalanceWideMetricCardsSplit(
            materialsMap[balanceFlowData.formulaId].name,
            (balanceFlowData.orderedRoutes || [])[0],
            balanceFlowData.balanceInstructions || [],
          ).even.map((item, index) => (
            <div key={index}>
              <WideMetricCard {...item} />
            </div>
          ))}
        </div>
        <div className="space-y-[0.5rem] flex flex-col justify-between basis-1/2">
          {getBalanceWideMetricCardsSplit(
            materialsMap[balanceFlowData.formulaId].name,
            (balanceFlowData.orderedRoutes || [])[0],
            balanceFlowData.balanceInstructions || [],
          ).odd.map((item, index) => (
            <div key={index}>
              <WideMetricCard {...item} />
            </div>
          ))}
        </div>
      </div>
      <BalanceFlowTransactionApprovalDisclaimerDiv />
      <div className="mt-auto space-x-4 flex">
        <div>
          <ButtonDesigned
            backButton
            className="py-2"
            style="outline"
            onButtonClick={() => setCurrentStepIndex((currIndex) => currIndex - 1)}
          ></ButtonDesigned>
        </div>
        <ButtonDesigned className="py-2" onButtonClick={() => updateBalanceFlow()}>
          Approve order
        </ButtonDesigned>
      </div>
    </div>
  );
};

const getTotalQuantity = (instructions: IBalanceInstruction[]): number => {
  return instructions.reduce((sum, item) => (sum += item.quantityPerContract * item.numberOfContracts), 0);
};

const getInstructionDateRange = (instructions: IBalanceInstruction[]): string => {
  if (instructions.length === 0) {
    throw new Error('Instructions array is empty');
  }

  const dates = instructions.map((instruction) => instruction.expiryDate.toDate().getTime());
  const start = new Timestamp(Math.min(...dates) / 1000, 0);
  const end = new Timestamp(Math.max(...dates) / 1000, 0);

  return formatDateRange(start, end);
};

const formatDateRange = (start: Timestamp, end: Timestamp): string => {
  const startDate = start.toDate();
  const endDate = end.toDate();
  const options: Intl.DateTimeFormatOptions = { month: 'short', year: '2-digit' };
  const startFormatted = startDate.toLocaleString('en-US', options);
  const endFormatted = endDate.toLocaleString('en-US', options);
  return `${startFormatted}-${endFormatted}`;
};
